/**
 * UpgradeModal Component
 *
 * Modal that appears when user hits generation limit or wants to upgrade.
 * Handles Stripe checkout flow for subscription upgrade.
 */

import { useState } from 'react';
import { useAuth } from '@clerk/clerk-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Check, Loader2, Sparkles, Zap } from 'lucide-react';

interface UpgradeModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  reason?: 'limit_reached' | 'subscription_needed' | 'upgrade';
  currentUsage?: number;
  usageLimit?: number;
}

const API_URL = import.meta.env.VITE_API_URL || '';

export function UpgradeModal({
  open,
  onOpenChange,
  reason = 'upgrade',
  currentUsage = 0,
  usageLimit = 25,
}: UpgradeModalProps) {
  const { getToken } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleUpgrade = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const token = await getToken();
      const response = await fetch(`${API_URL}/api/stripe/create-checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to create checkout session');
      }

      // Redirect to Stripe Checkout
      if (data.data?.url) {
        window.location.href = data.data.url;
      } else {
        throw new Error('No checkout URL returned');
      }
    } catch (err) {
      console.error('Upgrade error:', err);
      setError(err instanceof Error ? err.message : 'Failed to start upgrade process');
      setIsLoading(false);
    }
  };

  const getTitle = () => {
    switch (reason) {
      case 'limit_reached':
        return 'Generation Limit Reached';
      case 'subscription_needed':
        return 'Subscription Required';
      default:
        return 'Upgrade to Pro';
    }
  };

  const getDescription = () => {
    switch (reason) {
      case 'limit_reached':
        return `You've used ${currentUsage} of ${usageLimit} free generations this month. Upgrade to Pro for unlimited AI-generated designs.`;
      case 'subscription_needed':
        return 'Subscribe to Pro to continue generating AI designs for your leads.';
      default:
        return 'Unlock unlimited AI-generated designs and take your lead conversion to the next level.';
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary" />
            {getTitle()}
          </DialogTitle>
          <DialogDescription>{getDescription()}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Pricing Card */}
          <div className="rounded-lg border-2 border-primary bg-primary/5 p-4">
            <div className="flex items-baseline justify-between">
              <span className="text-lg font-semibold">Pro Plan</span>
              <div>
                <span className="text-3xl font-bold">$97</span>
                <span className="text-muted-foreground">/month</span>
              </div>
            </div>

            <ul className="mt-4 space-y-2">
              <li className="flex items-center gap-2 text-sm">
                <Check className="h-4 w-4 text-green-600" />
                <span>10 AI generation credits per month</span>
              </li>
              <li className="flex items-center gap-2 text-sm">
                <Check className="h-4 w-4 text-green-600" />
                <span>Unused credits roll over monthly</span>
              </li>
              <li className="flex items-center gap-2 text-sm">
                <Check className="h-4 w-4 text-green-600" />
                <span>Buy credit packs as needed</span>
              </li>
              <li className="flex items-center gap-2 text-sm">
                <Check className="h-4 w-4 text-green-600" />
                <span>Unlimited qualified leads</span>
              </li>
              <li className="flex items-center gap-2 text-sm">
                <Check className="h-4 w-4 text-green-600" />
                <span>Custom branding & widget</span>
              </li>
              <li className="flex items-center gap-2 text-sm">
                <Check className="h-4 w-4 text-green-600" />
                <span>Priority support</span>
              </li>
            </ul>
          </div>

          {/* Pro Benefits Summary */}
          <div className="rounded-lg border bg-muted/30 p-4">
            <div className="flex items-baseline justify-between">
              <span className="font-medium text-muted-foreground">Why Go Pro?</span>
            </div>
            <ul className="mt-2 space-y-1">
              <li className="flex items-center gap-2 text-sm text-muted-foreground">
                <Check className="h-4 w-4" />
                <span>No per-lead fees</span>
              </li>
              <li className="flex items-center gap-2 text-sm text-muted-foreground">
                <Check className="h-4 w-4" />
                <span>Month-to-month—cancel anytime</span>
              </li>
            </ul>
          </div>

          {error && (
            <p className="text-sm text-destructive">{error}</p>
          )}
        </div>

        <DialogFooter className="flex-col gap-2 sm:flex-col">
          <Button
            onClick={handleUpgrade}
            disabled={isLoading}
            className="w-full"
            size="lg"
          >
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Redirecting to checkout...
              </>
            ) : (
              <>
                <Zap className="mr-2 h-4 w-4" />
                Upgrade to Pro - $97/month
              </>
            )}
          </Button>
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={isLoading}
            className="w-full"
          >
            Maybe later
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default UpgradeModal;
